import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { lastValueFrom, Subject } from 'rxjs';
import { assert, LoaderService } from 'ats-ui-lib';
import { TranslateService } from '@ngx-translate/core';
import { PoNotificationService, PoToasterOrientation } from '@po-ui/ng-components';
import { takeUntil } from 'rxjs/operators';
import { BehavioralAssessmentService } from '../../root/behavioral-assessment/behavioral-assessment.service';

@Component({
  selector: 'ats-behavioral-assessment-answer-redirect',
  templateUrl: './behavioral-assessment-answer-redirect.component.html',
  styleUrls: ['./behavioral-assessment-answer-redirect.component.css'],
  standalone: false,
})
export class BehavioralAssessmentAnswerRedirectComponent implements OnInit, OnDestroy {
  private onDestroySubject = new Subject<void>();

  @Output() finishedRedirection = new EventEmitter<void>();

  constructor(
    private behavioralAssessmentService: BehavioralAssessmentService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private window: Window,
    private translateService: TranslateService,
    private toasterService: PoNotificationService,
    private loaderService: LoaderService,
  ) {}

  ngOnDestroy(): void {
    this.onDestroySubject.next();
  }

  ngOnInit(): void {
    this.activatedRoute.paramMap
      .pipe(takeUntil(this.onDestroySubject))
      .subscribe(this.onRouteChange.bind(this));
  }

  private async onRouteChange(paramMap: ParamMap): Promise<void> {
    try {
      this.loaderService.show();
      const id = paramMap.get('id');
      if (id == null) {
        await this.redirectOnError('l-behavioral-assessment-not-found');
        return;
      }
      const result = await lastValueFrom(this.behavioralAssessmentService.getAnswerLink(id));
      if (result == null) {
        await this.redirectOnError('l-behavioral-assessment-not-found');
        return;
      }
      if (!result.isAuthorized) {
        await this.redirectOnError('l-not-authorized');
        return;
      }
      assert(result.answerLink);
      this.window.location.href = result.answerLink;
    } catch (e) {
      this.loaderService.hide();
      throw e;
    } finally {
      this.finishedRedirection.emit();
    }
  }

  private redirectOnError(message: string): Promise<boolean> {
    this.loaderService.hide();
    const translatedMessage = this.translateService.instant(message);
    this.toasterService.warning({
      message: translatedMessage,
      orientation: PoToasterOrientation.Top,
    });
    return this.router.navigate(['main', 'home']);
  }
}
